/*
 * ─── คลังรูปทรง (องค์ประกอบ → รูปทรง, choices 25 ต.ค. 2569: ก หมวด + "ดูทั้งหมด" · ก สีพื้น + เส้นขอบ + พิมพ์ข้อความใน
 * รูปทรง · ก 10 หมวด หมวดละ ~10) — every shape is drawn here by code (none is copied from another app): an SVG path in a
 * 100 × 100 box. No DOM, no Fabric, so the tests load this.
 * A shape goes into the work as one Fabric path (one colour, an outline, vector in print PDFs).
 */

type Pt = [number, number];
const f = (n: number) => Math.round(n * 100) / 100;
const TAU = Math.PI * 2;

/** a polygon */
function poly(pts: Pt[]): string {
  return 'M' + pts.map(p => `${f(p[0])} ${f(p[1])}`).join(' L') + ' Z';
}

/** a polygon with round corners: `r` per corner (or one for all), never more than half of either side */
function roundPoly(pts: Pt[], r: number | number[]): string {
  const n = pts.length;
  const at = (i: number) => pts[(i + n) % n];
  const toward = (v: Pt, p: Pt, d: number): Pt => {
    const len = Math.hypot(p[0] - v[0], p[1] - v[1]) || 1;
    const k = Math.min(d / len, 0.5);
    return [v[0] + (p[0] - v[0]) * k, v[1] + (p[1] - v[1]) * k];
  };
  let d = '';
  for (let i = 0; i < n; i++) {
    const v = at(i), rr = Array.isArray(r) ? r[i] : r;
    if (!rr) { d += `${i ? ' L' : 'M'}${f(v[0])} ${f(v[1])}`; continue; }
    const a = toward(v, at(i - 1), rr), b = toward(v, at(i + 1), rr);
    d += `${i ? ' L' : 'M'}${f(a[0])} ${f(a[1])} Q${f(v[0])} ${f(v[1])} ${f(b[0])} ${f(b[1])}`;
  }
  return d + ' Z';
}

/** a closed outline from a radius per angle (θ from the top, clockwise), around (cx, cy) */
function polarPts(r: (t: number) => number, n = 240, cx = 50, cy = 50): Pt[] {
  return Array.from({ length: n }, (_, i) => {
    const t = i / n * TAU;
    const rr = r(t);
    return [cx + Math.sin(t) * rr, cy - Math.cos(t) * rr] as Pt;
  });
}
const polar = (r: (t: number) => number, n = 240, cx = 50, cy = 50) => poly(polarPts(r, n, cx, cy));

/** a circle (a sub-path: a hole when the shape is drawn even-odd) */
const circle = (cx: number, cy: number, r: number) =>
  `M${f(cx - r)} ${f(cy)} A${f(r)} ${f(r)} 0 1 0 ${f(cx + r)} ${f(cy)} A${f(r)} ${f(r)} 0 1 0 ${f(cx - r)} ${f(cy)} Z`;

/** how far a ray from the centre at angle t reaches inside a union of circles (the circles must cover the centre) */
function circlesReach(circles: Array<[number, number, number]>, cx: number, cy: number) {
  return (t: number) => {
    const dx = Math.sin(t), dy = -Math.cos(t);
    let best = 0;
    for (const [x, y, r] of circles) {
      const ox = cx - x, oy = cy - y;
      const b = ox * dx + oy * dy, c = ox * ox + oy * oy - r * r, disc = b * b - c;
      if (disc >= 0) best = Math.max(best, -b + Math.sqrt(disc));
    }
    return best;
  };
}

/** a star of `n` points, the inner corners at `inner` of the outer radius; `round` softens the corners */
function star(n: number, inner: number, round = 0, R = 48): string {
  const pts: Pt[] = [];
  for (let i = 0; i < n * 2; i++) {
    const t = i / (n * 2) * TAU, r = i % 2 ? R * inner : R;
    pts.push([50 + Math.sin(t) * r, 50 - Math.cos(t) * r]);
  }
  return round ? roundPoly(pts, round) : poly(pts);
}

/** an asterisk: `n` straight arms (through the centre: 2n ends) of half width `w`, reaching `L` */
function asterisk(n: number, w: number, L = 48, turn = 0): string {
  return polar(t => {
    let best = 0;
    for (let k = 0; k < n; k++) {
      const a = t - (turn + k / n * Math.PI);
      const s = Math.abs(Math.sin(a)), c = Math.abs(Math.cos(a));
      best = Math.max(best, Math.min(s > 1e-9 ? w / s : Infinity, c > 1e-9 ? L / c : Infinity));
    }
    return best;
  }, 1440);
}

/** a flower of `n` petals: `base` how far the petals are cut in, `p` how round they are; `hole` a centre cut out */
function flower(n: number, base: number, p: number, hole = 0): string {
  const d = polar(t => 48 * (base + (1 - base) * Math.pow(Math.abs(Math.cos(n * t / 2)), p)), 720);
  return hole ? d + ' ' + circle(50, 50, hole) : d;
}

/** a cog of `n` teeth (outer R, root r), a hole of radius `hole` */
function gear(n: number, R: number, r: number, hole: number, flat = 0.35): string {
  const ramp = (1 - 2 * flat) / 4;
  const d = polar(t => {
    const ph = (t / TAU * n) % 1;
    if (ph < flat) return R;
    if (ph < flat + ramp) return R - (R - r) * (ph - flat) / ramp;
    if (ph < 1 - ramp) return r;
    return r + (R - r) * (ph - (1 - ramp)) / ramp;
  }, n * 40);
  return hole ? d + ' ' + circle(50, 50, hole) : d;
}

/** a heart: the classic curve, stretched by (sx, sy), turned by `turn` degrees */
function heart(sx = 1, sy = 1, turn = 0, round = 0): string {
  const pts: Pt[] = [];
  for (let i = 0; i < 240; i++) {
    const t = i / 240 * TAU;
    let x = 16 * Math.pow(Math.sin(t), 3);
    let y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    if (round) { const k = Math.hypot(x, y + 2) || 1; x = x * (1 - round) + (x / k) * 15 * round; y = y * (1 - round) + ((y + 2) / k * 15 - 2) * round; }
    pts.push([x * sx, y * sy]);
  }
  return fitPts(pts, turn);
}

/** a drop: pointed at the top; `m` how slim the point is, `w` how wide */
function drop(m: number, w = 1, turn = 0): string {
  const pts: Pt[] = [];
  for (let i = 0; i < 240; i++) {
    const t = i / 240 * TAU;
    pts.push([Math.sin(t) * Math.pow(Math.sin(t / 2), m) * w, -Math.cos(t)]);
  }
  return fitPts(pts, turn);
}

/** points turned by `turn` degrees and fitted into 2–98 (keeping their proportions) */
function fitPts(pts: Pt[], turn = 0): string {
  const a = turn * Math.PI / 180;
  const p = pts.map(([x, y]) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)] as Pt);
  const xs = p.map(q => q[0]), ys = p.map(q => q[1]);
  const x0 = Math.min(...xs), y0 = Math.min(...ys), w = Math.max(...xs) - x0, h = Math.max(...ys) - y0;
  const k = 96 / Math.max(w, h);
  const ox = 50 - w * k / 2, oy = 50 - h * k / 2;
  return poly(p.map(([x, y]) => [ox + (x - x0) * k, oy + (y - y0) * k] as Pt));
}

/** a cloud: circles over a flat-ish base */
const cloud = (circles: Array<[number, number, number]>, cx = 50, cy = 60) => polar(circlesReach(circles, cx, cy), 720, cx, cy);

/** a speech bubble: a box with round corners `r` and a tail at the bottom, from x `a` to `b`, its tip at (tx, ty) */
function bubbleBox(x0: number, y0: number, x1: number, y1: number, r: number, a: number, b: number, tx: number, ty: number): string {
  return roundPoly([[x0, y0], [x1, y0], [x1, y1], [b, y1], [tx, ty], [a, y1], [x0, y1]], [r, r, r, 0, 0, 0, r]);
}

/** an oval bubble: an ellipse (rx, ry) with a tail whose base spans the angles a0–a1 (degrees from the top) and tip (tx, ty) */
function bubbleOval(rx: number, ry: number, cy: number, a0: number, a1: number, tx: number, ty: number): string {
  const pts: Pt[] = [];
  let tailDone = false;
  for (let i = 0; i < 240; i++) {
    const deg = i / 240 * 360;
    if (deg > a0 && deg < a1) { if (!tailDone) { pts.push([tx, ty]); tailDone = true; } continue; }
    const t = deg * Math.PI / 180;
    pts.push([50 + Math.sin(t) * rx, cy - Math.cos(t) * ry]);
  }
  return poly(pts);
}

/** a wavy band across the box: `waves` humps, `amp` high, `thick` thick */
function waveBand(waves: number, amp: number, thick: number): string {
  const top: Pt[] = [], n = 120;
  for (let i = 0; i <= n; i++) { const x = 2 + i / n * 96; top.push([x, 50 - thick / 2 + Math.sin(i / n * waves * TAU) * amp]); }
  const bottom = top.map(([x, y]) => [x, y + thick] as Pt).reverse();
  return poly([...top, ...bottom]);
}

/** a sparkle: four points joined by curves going in (a rounder one for a smaller power) */
function sparkle(p: number): string {
  const pts: Pt[] = [];
  for (let i = 0; i < 240; i++) {
    const t = i / 240 * TAU, s = Math.sin(t), c = Math.cos(t);
    pts.push([Math.sign(s) * Math.pow(Math.abs(s), p), -Math.sign(c) * Math.pow(Math.abs(c), p)]);
  }
  return fitPts(pts);
}

/** a shout: a jagged oval (24 points in and out) */
function shout(): string {
  const pts: Pt[] = [];
  for (let i = 0; i < 24; i++) {
    const t = i / 24 * TAU, r = i % 2 ? 0.78 : 1;
    pts.push([Math.sin(t) * r * 1.25, -Math.cos(t) * r]);
  }
  return fitPts(pts);
}

/** a soft blob: a circle with gentle bumps */
const blob = (a: number, b: number, c: number) => polar(t => 40 + a * Math.sin(3 * t + c) + b * Math.sin(5 * t + c * 2), 360);

export interface ShapeDef { id: string; th: string; d: string; evenOdd?: boolean; kw?: string }
export interface ShapeCat { id: string; th: string; kw: string; shapes: ShapeDef[] }

/** the shapes, by the panel's categories (Thai names; `kw` = more words to find them by) */
export const SHAPE_CATS: ShapeCat[] = [
  { id: 'free', th: 'รูปทรงอิสระ', kw: 'อิสระ โมเดิร์น free', shapes: [
    { id: 'quatrefoil', th: 'สี่กลีบมน', d: polar(circlesReach([[30, 30, 22], [70, 30, 22], [30, 70, 22], [70, 70, 22], [50, 50, 30]], 50, 50), 720) },
    { id: 'pill-stack', th: 'แคปซูลซ้อน', d: roundPoly([[4, 14], [96, 14], [96, 48], [74, 48], [74, 52], [96, 52], [96, 86], [4, 86], [4, 52], [26, 52], [26, 48], [4, 48]], [17, 17, 3, 0, 0, 3, 17, 17, 3, 0, 0, 3]) },
    { id: 'half-moons', th: 'ครึ่งวงกลมสามชั้น', d: `M50 4 A46 46 0 0 0 50 96 Z M70 8 A42 42 0 0 0 70 92 Z M88 12 A38 38 0 0 0 88 88 Z` },
    { id: 'arch', th: 'ซุ้มโค้ง', d: 'M10 96 L10 50 A40 40 0 0 1 90 50 L90 96 Z' },
    { id: 'leaf-corner', th: 'มุมโค้งหนึ่งด้าน', d: 'M4 4 A92 92 0 0 1 96 96 L4 96 Z' },
    { id: 'cut-corners', th: 'ตัดมุมทแยง', d: poly([[30, 4], [96, 4], [96, 70], [70, 96], [4, 96], [4, 30]]) },
    { id: 'sparkle4', th: 'ประกายสี่แฉก', d: sparkle(2.2) },
    { id: 'twin-sparkle', th: 'ประกายคู่', d: scaled(sparkle(2.4), 0.5, 1, 25) + ' ' + scaled(sparkle(2.4), 0.5, 49, 25) },
    { id: 'flower-square', th: 'ดอกไม้ช่องกลาง', d: polar(circlesReach([[50, 23, 22], [77, 50, 22], [50, 77, 22], [23, 50, 22], [50, 50, 28]], 50, 50), 720) + ' ' + poly([[40, 40], [60, 40], [60, 60], [40, 60]]), evenOdd: true },
    { id: 'squircle', th: 'สี่เหลี่ยมมนนุ่ม', d: polar(t => 46 / Math.pow(Math.pow(Math.abs(Math.sin(t)), 4) + Math.pow(Math.abs(Math.cos(t)), 4), 0.25), 360) },
    { id: 'ticket', th: 'ตั๋ว', d: 'M4 20 L96 20 L96 40 A10 10 0 0 0 96 60 L96 80 L4 80 L4 60 A10 10 0 0 0 4 40 Z' },
    { id: 'pill', th: 'แคปซูล', d: roundPoly([[4, 28], [96, 28], [96, 72], [4, 72]], 22) }
  ] },
  { id: 'organic', th: 'รูปทรงแบบออร์แกนิก', kw: 'ดอกไม้ ธรรมชาติ ออร์แกนิก organic flower', shapes: [
    { id: 'flower8-thin', th: 'ดอกไม้กลีบเรียว', d: flower(8, 0.08, 2.2) },
    { id: 'flower6', th: 'ดอกไม้หกกลีบ', d: flower(6, 0.12, 1.2) },
    { id: 'flower10', th: 'ดอกไม้สิบกลีบ', d: flower(10, 0.25, 1.4) },
    { id: 'flower8-hole', th: 'ดอกไม้มีรู', d: flower(8, 0.45, 0.6, 9), evenOdd: true },
    { id: 'clover', th: 'ใบโคลเวอร์', d: polar(t => 48 * (0.35 + 0.65 * Math.pow(Math.abs(Math.cos(2 * t)), 0.5)), 720) },
    { id: 'petals4', th: 'กลีบใบสี่ทิศ', d: polar(t => 48 * Math.pow(Math.abs(Math.sin(2 * t)), 0.9), 720) },
    { id: 'leaf', th: 'ใบไม้', d: 'M4 96 C4 40 40 4 96 4 C96 60 60 96 4 96 Z' },
    { id: 'wave', th: 'เส้นคลื่น', d: waveBand(3, 7, 14) },
    { id: 'wave-soft', th: 'คลื่นอ่อน', d: waveBand(1, 9, 16) },
    { id: 'blob1', th: 'ก้อนนุ่ม 1', d: blob(5, 3, 0.4) },
    { id: 'blob2', th: 'ก้อนนุ่ม 2', d: blob(7, 2, 1.7) },
    { id: 'blob3', th: 'ก้อนนุ่ม 3', d: blob(4, 5, 2.9) }
  ] },
  { id: 'star', th: 'ดาว และดอกจัน', kw: 'ดาว ดอกจัน ประกาย star asterisk burst', shapes: [
    { id: 'star5', th: 'ดาวห้าแฉก', d: star(5, 0.42) },
    { id: 'star5-round', th: 'ดาวมุมมน', d: star(5, 0.45, 5) },
    { id: 'star6', th: 'ดาวหกแฉก', d: star(6, 0.55) },
    { id: 'star8', th: 'ดาวแปดแฉก', d: star(8, 0.6) },
    { id: 'burst12', th: 'ป้ายระเบิด 12', d: star(12, 0.78) },
    { id: 'burst24', th: 'ป้ายหยัก 24', d: star(24, 0.86) },
    { id: 'seal', th: 'ตราหยักมน', d: polar(t => 44 + 4 * Math.cos(16 * t), 720) },
    { id: 'asterisk6', th: 'ดอกจันหกแฉก', d: asterisk(3, 7) },
    { id: 'asterisk8', th: 'ดอกจันแปดแฉก', d: asterisk(4, 7) },
    { id: 'asterisk-bold', th: 'ดอกจันหนา', d: asterisk(3, 12) },
    { id: 'asterisk5', th: 'ดอกจันห้าแฉก', d: polar(t => { let b = 0; for (let k = 0; k < 5; k++) { const a = t - k / 5 * TAU; const c = Math.cos(a), s = Math.abs(Math.sin(a)); if (c > 0) b = Math.max(b, Math.min(s > 1e-9 ? 8 / s : Infinity, 48 / c)); } return Math.max(b, 9); }, 1440) },
    { id: 'sun', th: 'พระอาทิตย์', d: star(16, 0.72, 0) }
  ] },
  { id: 'gear', th: 'ฟันเฟือง', kw: 'เฟือง เครื่องจักร ตั้งค่า gear cog', shapes: [
    { id: 'gear12-big', th: 'เฟือง 12 ฟัน รูใหญ่', d: gear(12, 48, 40, 28), evenOdd: true },
    { id: 'gear10', th: 'เฟือง 10 ฟัน', d: gear(10, 48, 38, 22), evenOdd: true },
    { id: 'gear16', th: 'เฟือง 16 ฟัน', d: gear(16, 48, 41, 18, 0.3), evenOdd: true },
    { id: 'gear8', th: 'เฟือง 8 ฟัน', d: gear(8, 48, 36, 18), evenOdd: true },
    { id: 'gear12-small', th: 'เฟือง รูเล็ก', d: gear(12, 48, 40, 9), evenOdd: true },
    { id: 'gear6', th: 'เฟือง 6 ฟัน', d: gear(6, 48, 34, 16, 0.3), evenOdd: true },
    { id: 'gear20', th: 'เฟืองละเอียด', d: gear(20, 48, 43, 24, 0.4), evenOdd: true },
    { id: 'gear-solid', th: 'เฟืองทึบ', d: gear(12, 48, 40, 0) }
  ] },
  { id: 'drop', th: 'หยดน้ำ', kw: 'หยดน้ำ น้ำ ฝน drop water', shapes: [
    { id: 'drop-slim', th: 'หยดน้ำเรียว', d: drop(3, 0.75) },
    { id: 'drop', th: 'หยดน้ำ', d: drop(2, 0.85) },
    { id: 'drop-wide', th: 'หยดน้ำอ้วน', d: drop(1.4, 1.05) },
    { id: 'drop-round', th: 'หยดน้ำกลม', d: drop(1, 1.15) },
    { id: 'drop-tilt', th: 'หยดน้ำเอียง', d: drop(2.2, 0.85, 25) },
    { id: 'drop-down', th: 'หยดน้ำกลับหัว', d: drop(2, 0.85, 180) },
    { id: 'drops3', th: 'หยดน้ำสามหยด', d: scaled(drop(2, 0.85), 0.5, 4, 30) + ' ' + scaled(drop(2, 0.85), 0.42, 48, 4) + ' ' + scaled(drop(2, 0.85), 0.36, 62, 58) }
  ] },
  { id: 'banner', th: 'แบนเนอร์', kw: 'ริบบิ้น ป้าย ธง banner ribbon flag', shapes: [
    { id: 'ribbon', th: 'ริบบิ้นปลายบาก', d: poly([[2, 28], [98, 28], [86, 50], [98, 72], [2, 72], [14, 50]]) },
    { id: 'bookmark-point', th: 'ป้ายปลายแหลม', d: poly([[18, 2], [82, 2], [82, 76], [50, 98], [18, 76]]) },
    { id: 'bookmark', th: 'ป้ายปลายบาก', d: poly([[10, 2], [90, 2], [90, 98], [50, 78], [10, 98]]) },
    { id: 'tag-point', th: 'ป้ายห้อยแหลม', d: poly([[26, 2], [74, 2], [74, 72], [50, 98], [26, 72]]) },
    { id: 'tag-notch', th: 'ป้ายห้อยบาก', d: poly([[22, 2], [78, 2], [78, 98], [50, 80], [22, 98]]) },
    { id: 'pennant', th: 'ธงสามเหลี่ยม', d: poly([[4, 10], [96, 50], [4, 90]]) },
    { id: 'swallow', th: 'ธงหางนกแอ่น', d: poly([[4, 14], [96, 14], [74, 50], [96, 86], [4, 86]]) },
    { id: 'arrow-banner', th: 'ป้ายลูกศร', d: poly([[2, 26], [80, 26], [98, 50], [80, 74], [2, 74], [16, 50]]) },
    { id: 'scroll', th: 'ริบบิ้นสองชั้น', d: poly([[18, 22], [82, 22], [82, 30], [98, 30], [90, 50], [98, 70], [72, 70], [72, 64], [28, 64], [28, 70], [2, 70], [10, 50], [2, 30], [18, 30]]) },
    { id: 'label-round', th: 'ป้ายมุมมน', d: roundPoly([[2, 26], [98, 26], [98, 74], [2, 74]], 10) }
  ] },
  { id: 'heart', th: 'หัวใจ', kw: 'หัวใจ รัก ความรัก heart love', shapes: [
    { id: 'heart', th: 'หัวใจ', d: heart() },
    { id: 'heart-wide', th: 'หัวใจกว้าง', d: heart(1.25, 1) },
    { id: 'heart-tall', th: 'หัวใจสูง', d: heart(0.85, 1.15) },
    { id: 'heart-round', th: 'หัวใจกลมมน', d: heart(1, 1, 0, 0.35) },
    { id: 'heart-tilt', th: 'หัวใจเอียง', d: heart(1, 1, -18) },
    { id: 'heart-sharp', th: 'หัวใจปลายแหลม', d: heart(1, 1.3) },
    { id: 'hearts2', th: 'หัวใจคู่', d: scaled(heart(), 0.6, 2, 6) + ' ' + scaled(heart(1, 1, 12), 0.5, 48, 46) }
  ] },
  { id: 'cloud', th: 'ก้อนเมฆ', kw: 'เมฆ ท้องฟ้า cloud sky', shapes: [
    { id: 'cloud-puffy', th: 'เมฆฟู', d: cloud([[28, 58, 20], [48, 44, 24], [70, 52, 20], [84, 64, 13], [16, 68, 12], [50, 66, 22]]) },
    { id: 'cloud-flat', th: 'เมฆฐานเรียบ', d: cloud([[30, 58, 16], [52, 46, 22], [74, 58, 16], [50, 66, 14], [14, 66, 10], [88, 66, 10]]) },
    { id: 'cloud-big', th: 'เมฆก้อนใหญ่', d: cloud([[36, 52, 26], [64, 50, 24], [20, 66, 14], [84, 66, 14], [50, 68, 18]]) },
    { id: 'cloud-two', th: 'เมฆสองยอด', d: cloud([[34, 54, 22], [62, 46, 28], [16, 68, 12], [86, 66, 12], [50, 68, 18]]) },
    { id: 'cloud-tall', th: 'เมฆยอดสูง', d: cloud([[50, 40, 30], [24, 64, 16], [76, 64, 16], [50, 68, 18]]) },
    { id: 'cloud-long', th: 'เมฆยาว', d: cloud([[18, 56, 14], [38, 48, 18], [60, 46, 18], [80, 54, 14], [50, 60, 14], [92, 60, 6], [6, 60, 6]], 50, 58) }
  ] },
  { id: 'bubble', th: 'กล่องคำพูด', kw: 'คำพูด แชท ความคิด speech bubble chat', shapes: [
    { id: 'bubble-square', th: 'กล่องคำพูดเหลี่ยม', d: bubbleBox(4, 6, 96, 70, 0, 18, 40, 16, 96) },
    { id: 'bubble-round', th: 'กล่องคำพูดมุมมน', d: bubbleBox(4, 6, 96, 70, 12, 20, 40, 16, 94) },
    { id: 'bubble-oval', th: 'กล่องคำพูดวงรี', d: bubbleOval(46, 32, 40, 200, 225, 18, 94) },
    { id: 'bubble-right', th: 'กล่องคำพูดหางขวา', d: bubbleBox(4, 6, 96, 70, 12, 60, 80, 86, 94) },
    { id: 'bubble-think', th: 'กล่องความคิด', d: cloud([[30, 34, 18], [50, 26, 20], [70, 34, 18], [76, 50, 14], [24, 50, 14], [50, 48, 20]], 50, 40) + ' ' + circle(22, 78, 7) + ' ' + circle(12, 92, 4) },
    { id: 'bubble-shout', th: 'กล่องตะโกน', d: shout() },
    { id: 'bubble-wide', th: 'กล่องข้อความยาว', d: bubbleBox(2, 20, 98, 70, 25, 40, 56, 48, 92) },
    { id: 'bubble-chat', th: 'กล่องแชท', d: bubbleBox(4, 10, 96, 76, 18, 8, 28, 2, 94) }
  ] },
  { id: 'flow', th: 'รูปทรงโฟลว์ชาร์ต', kw: 'โฟลว์ชาร์ต แผนผัง ขั้นตอน flowchart diagram', shapes: [
    { id: 'process', th: 'กระบวนการ', d: poly([[4, 22], [96, 22], [96, 78], [4, 78]]) },
    { id: 'terminator', th: 'เริ่ม/จบ', d: roundPoly([[4, 28], [96, 28], [96, 72], [4, 72]], 22) },
    { id: 'decision', th: 'การตัดสินใจ', d: roundPoly([[50, 6], [96, 50], [50, 94], [4, 50]], 3) },
    { id: 'data', th: 'ข้อมูล (สี่เหลี่ยมด้านขนาน)', d: roundPoly([[22, 22], [98, 22], [78, 78], [2, 78]], 3) },
    { id: 'document', th: 'เอกสาร', d: 'M4 14 L96 14 L96 76 C72 66 52 98 4 82 Z' },
    { id: 'prep', th: 'การเตรียม (หกเหลี่ยม)', d: poly([[20, 24], [80, 24], [98, 50], [80, 76], [20, 76], [2, 50]]) },
    { id: 'manual', th: 'งานมือ (สี่เหลี่ยมคางหมู)', d: roundPoly([[4, 20], [96, 20], [82, 80], [18, 80]], 3) },
    { id: 'delay', th: 'หน่วงเวลา', d: 'M4 20 L60 20 A30 30 0 0 1 60 80 L4 80 Z' },
    { id: 'merge', th: 'รวม (สามเหลี่ยมคว่ำ)', d: roundPoly([[6, 12], [94, 12], [50, 92]], 3) },
    { id: 'offpage', th: 'เชื่อมหน้าอื่น', d: roundPoly([[16, 4], [84, 4], [84, 64], [50, 96], [16, 64]], 3) },
    { id: 'card', th: 'บัตร (ตัดมุม)', d: poly([[24, 20], [96, 20], [96, 80], [4, 80], [4, 40]]) },
    { id: 'stored', th: 'ข้อมูลที่เก็บไว้', d: 'M20 20 L96 20 A14 30 0 0 0 96 80 L20 80 A14 30 0 0 1 20 20 Z' }
  ] }
];

/** a shape scaled by `k` and moved to (x, y) — for shapes made of a few smaller ones */
function scaled(d: string, k: number, x: number, y: number): string {
  // only absolute M/L/Z points made by poly(): "x y" pairs
  return d.replace(/(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)/g, (_, a: string, b: string) => `${f(x + Number(a) * k)} ${f(y + Number(b) * k)}`);
}

/** every shape with its category, for search and for opening by id */
export const ALL_SHAPES: Array<ShapeDef & { cat: string }> = SHAPE_CATS.flatMap(c => c.shapes.map(s => ({ ...s, cat: c.id })));
export const shapeById = (id: unknown) => ALL_SHAPES.find(s => s.id === id) ?? null;
/** the words a shape is found by (its name, its category's name and words) */
export const shapeWords = (s: ShapeDef & { cat: string }) => {
  const c = SHAPE_CATS.find(x => x.id === s.cat)!;
  return `${s.th} ${c.th} ${c.kw} ${s.kw ?? ''}`.toLowerCase();
};
/** an SVG of a shape (the panel's tiles): fill in `color` */
export const shapeSvg = (s: ShapeDef, color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path d="${s.d}" fill="${color}"${s.evenOdd ? ' fill-rule="evenodd"' : ''}/></svg>`;

/** a text typed in a shape: its box is this part of the shape's width, its letters this part of the shape's height */
export const SHAPE_TEXT_WIDTH = 0.7;
export const SHAPE_TEXT_SIZE = 0.16;
